import { useMemo } from 'react'
import {
  Message,
  IMessage,
  RawTextMessage,
  RawImageMessage,
  RawVideoMessage,
  RawFileMessage,
  RawWappMessage,
  RawAudioMessage,
  CallMessageAttachment,
  MessageSender,
} from '@weblite/ts-action/message'
import { getWappTheme } from '$common/wapp-bar-theme'
import { MessagePreviewMessageDescriptorTypography } from '$common/messsage-preview'
import { useWapp } from '$logic/wapps/react-hook'
import { useEventMessageText } from '$logic/message/react-hook'
import { getCallMessageStatusText } from '$logic/call'
import { t } from '$i18n'

interface MessageDescriptorComponentProps<M> {
  message: M
  fromMe: boolean
}

function TextMessage({
  message: { body },
  fromMe,
}: MessageDescriptorComponentProps<RawTextMessage>) {
  return (
    <MessagePreviewMessageDescriptorTypography fromMe={fromMe}>
      {body}
    </MessagePreviewMessageDescriptorTypography>
  )
}

function VoiceMessage({ fromMe }: { fromMe: boolean }) {
  return (
    <MessagePreviewMessageDescriptorTypography fromMe={fromMe}>
      {t('message.VOICE')}
    </MessagePreviewMessageDescriptorTypography>
  )
}

function ImageMessage({
  message,
  fromMe,
}: MessageDescriptorComponentProps<RawImageMessage>) {
  const caption = message.body

  return (
    <MessagePreviewMessageDescriptorTypography
      fromMe={fromMe}
    >
      {caption || t('message.IMAGE')}
    </MessagePreviewMessageDescriptorTypography>
  )
}

function VideoMessage({
  message,
  fromMe,
}: MessageDescriptorComponentProps<RawVideoMessage>) {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const caption = message.body

  return (
    <MessagePreviewMessageDescriptorTypography
      fromMe={fromMe}
    >
      {caption || t('message.VIDEO')}
    </MessagePreviewMessageDescriptorTypography>
  )
}

function FileMessage({
  message,
  fromMe,
}: MessageDescriptorComponentProps<RawFileMessage>) {
  const caption = message.body
  const fileName = message.attachment.filename

  return (
    <MessagePreviewMessageDescriptorTypography
      fromMe={fromMe}
    >
      {caption || fileName}
    </MessagePreviewMessageDescriptorTypography>
  )
}

function AudioMessage({
  message,
  fromMe,
}: MessageDescriptorComponentProps<RawAudioMessage>) {
  const fileName = message.attachment.filename

  return (
    <MessagePreviewMessageDescriptorTypography fromMe={fromMe}>
      {fileName}
    </MessagePreviewMessageDescriptorTypography>
  )
}

function ContactMessage({ fromMe }: { fromMe: boolean }) {
  return (
    <MessagePreviewMessageDescriptorTypography fromMe={fromMe}>
      {t('message.CONTACT')}
    </MessagePreviewMessageDescriptorTypography>
  )
}

function CallMessage({
  message,
  fromMe,
}: MessageDescriptorComponentProps<IMessage>) {
  const { id: senderId } = message.sender as MessageSender
  const { status } = message.attachment as CallMessageAttachment

  const statusText = useMemo(() => getCallMessageStatusText(status, senderId), [
    senderId,
    status,
  ])

  return (
    <MessagePreviewMessageDescriptorTypography fromMe={fromMe}>
      {statusText}
    </MessagePreviewMessageDescriptorTypography>
  )
}

function WappMessage({
  message,
  fromMe,
}: MessageDescriptorComponentProps<RawWappMessage>) {
  const { wappId } = message.attachment!
  useWapp(wappId)
  const wappTheme = getWappTheme(wappId)

  return (
    <MessagePreviewMessageDescriptorTypography fromMe={fromMe}>
      {wappTheme.name}
    </MessagePreviewMessageDescriptorTypography>
  )
}

function EventMessage({
  message,
  fromMe,
}: {
  message: IMessage
  fromMe: boolean
}) {
  const { eventText } = useEventMessageText(message)

  return (
    <MessagePreviewMessageDescriptorTypography fromMe={fromMe}>
      {eventText}
    </MessagePreviewMessageDescriptorTypography>
  )
}

function DeletedMessage({ fromMe }: { fromMe: boolean }) {
  return (
    <MessagePreviewMessageDescriptorTypography fromMe={fromMe}>
      {t('message.deleted')}
    </MessagePreviewMessageDescriptorTypography>
  )
}

const messageDescriptorComponentMap = {
  TEXT: TextMessage,
  VOICE: VoiceMessage,
  IMAGE: ImageMessage,
  FILE: FileMessage,
  WAPP: WappMessage,
  AUDIO: AudioMessage,
  VIDEO: VideoMessage,
  CONTACT: ContactMessage,
  CALL: CallMessage,
}

export function MessageDescriptor({
  message,
  fromMe,
}: {
  message: Message
  fromMe: boolean
}) {
  if (message.patches?.deleted) return <DeletedMessage fromMe={fromMe} />

  if (message.type === 'EVENT')
    return <EventMessage message={message as IMessage} fromMe={fromMe} />

  const MessageDescriptorComponent = messageDescriptorComponentMap[message.type]
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return <MessageDescriptorComponent message={message} fromMe={fromMe} />
}
